import Banner from '@/components/Banner';
import Chapters, { Chapter } from '@/components/Chapters';
import ImageGallery from '@/components/ImageGallery';
import { Reveal } from '@/components/Reveal';
import ShareDialog from '@/components/ShareDialog';
import Typography from '@/components/ui/typography';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';
import { siteStats } from '@/lib/siteStats';

const chapters: Chapter[] = [
  {
    title: 'Zero Investment, Infinite Impact',
    tagline: 'Unveiling the Journey',
    description:
      'Amidst daunting odds, in 2011, a humble academy sprang to life atop an open terrace in Trichy, Tamil Nadu. What ensued was not just a tale of growth but an odyssey of determination.',
  },
  {
    title: 'Branches Blossoming',
    tagline: 'Expanding Horizons',
    description:
      'In a mere year, the academy burgeoned into EIGHT thriving branches across Trichy and beyond, reflecting an insatiable quest for knowledge and skill.',
  },
  {
    title: 'Embracing Inclusivity',
    tagline: 'A Beacon of Hope',
    description:
      'With a vision of inclusivity, the academy franchised FOUR additional branches, extending its warmth to eager minds far and wide.',
  },
  {
    title: 'A Tapestry of Learning',
    tagline: 'Courses Galore',
    description: `Offering a diverse spectrum of ${siteStats.courses}+ courses under one roof, from fashion to cultural studies, the academy's commitment to skill enrichment knows no bounds.`,
  },
  {
    title: 'Convenience Redefined',
    tagline: 'Services at Your Doorstep',
    description:
      'Beyond education, the academy offers tailor and beautician services at the doorstep, ensuring accessibility and convenience.',
  },
  {
    title: 'Empowering Lives',
    tagline: 'Nurturing Entrepreneurs',
    description:
      'By nurturing women entrepreneurs and fostering empowerment, the academy paints a masterpiece of transformation in countless lives.',
  },
  {
    title: 'Hallmarks of Excellence',
    tagline: 'Recognition Achieved',
    description:
      "Attaining ISO Certification and Trademark Registration, the academy's dedication to excellence shines brightly.",
  },
  {
    title: 'A Magnificent Journey',
    tagline: 'Inspiring Change',
    description:
      'As we celebrate this remarkable journey, let us draw inspiration from its beauty and strive to make our world more vibrant and inspiring, one step at a time.',
  },
];

const Academy = ({ images }: { images: string[] }) => {
  const urls = images.map((image) => `/storage/${image}`);

  // images[0] is the banner; the chapters take what follows, and whatever is
  // left over becomes the gallery.
  const chapterImages = urls.slice(1, chapters.length + 1);
  const galleryImages = urls.slice(chapters.length + 1);

  return (
    <>
      <Banner
        image={urls[0]}
        alt="Samuthrika Academy"
        tagline="From an open terrace to infinite horizons"
        title="About Academy"
        description="Genuinely enthralled to unveil a profoundly inspiring and exquisitely beautiful tale of success."
      >
        <ShareDialog
          url={window.location.href}
          title="About Academy"
          content="Unveiling the Journey of Excellence: From an Open Terrace to Infinite Horizons"
        />
      </Banner>

      <Band tone="raised" pattern="lattice">
        <SectionLayout space="lg">
          <Chapters chapters={chapters} images={chapterImages} />
        </SectionLayout>
      </Band>

      {galleryImages.length > 0 && (
        <Band tone="raised" seam>
          <SectionLayout space="lg">
            <Reveal className="flex flex-col gap-3">
              <Typography.Eyebrow>From the branches</Typography.Eyebrow>
              <Typography.Display2 className="text-2xl md:text-3xl lg:text-4xl">
                The academy, in pictures
              </Typography.Display2>
            </Reveal>

            <ImageGallery
              images={galleryImages}
              alt="Photographs from Samuthrika Academy's branches"
            />
          </SectionLayout>
        </Band>
      )}
    </>
  );
};

export default Academy;
